import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import type { ChapterDto, ParagraphDto } from '../../api/queries/books';
import { useParagraphs } from '../../api/queries/books';
import { useParagraphImages } from '../../api/queries/media';
import { useParagraphAudio } from '../../api/queries/audio';
import { AppImage } from '../common/AppImage';
import { AudioPlayer } from '../common/AudioPlayer';

interface ReaderPreviewProps {
  bookId: string;
  chapters: ChapterDto[];
  currentChapterId: string;
  onChapterChange: (chapterId: string) => void;
  onClose: () => void;
}

const FONT_SIZES = [14, 16, 18, 20, 22] as const;

/**
 * Full-width reader preview that replaces the editor center panel when toggled.
 * Centered content with serif font, inline images, and audio playback.
 */
export function ReaderPreview({
  bookId: _bookId,
  chapters,
  currentChapterId,
  onChapterChange,
  onClose,
}: ReaderPreviewProps) {
  const [fontSize, setFontSize] = useState<number>(18);
  const [darkMode, setDarkMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const paragraphs = useParagraphs(currentChapterId);
  const currentChapter = useMemo(
    () => chapters.find((c) => c.id === currentChapterId),
    [chapters, currentChapterId],
  );
  const currentIndex = useMemo(
    () => chapters.findIndex((c) => c.id === currentChapterId),
    [chapters, currentChapterId],
  );

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < chapters.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) {
      onChapterChange(chapters[currentIndex - 1]!.id);
    }
  }, [hasPrev, currentIndex, chapters, onChapterChange]);

  const goNext = useCallback(() => {
    if (hasNext) {
      onChapterChange(chapters[currentIndex + 1]!.id);
    }
  }, [hasNext, currentIndex, chapters, onChapterChange]);

  // Scroll to top on chapter change
  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentChapterId]);

  const bgColor = darkMode ? '#1a1a2e' : '#faf9f6';
  const textColor = darkMode ? '#e0e0e0' : '#2c2c2c';
  const headerBg = darkMode ? '#16213e' : '#ffffff';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        bgcolor: bgColor,
        display: 'flex',
        flexDirection: 'column',
        transition: 'background-color 0.3s ease',
      }}
    >
      {/* Header toolbar */}
      <Paper
        elevation={1}
        sx={{
          px: 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderRadius: 0,
          bgcolor: headerBg,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Reader Preview
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* Font size controls */}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Font
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            {FONT_SIZES.map((size) => (
              <Button
                key={size}
                variant={fontSize === size ? 'contained' : 'outlined'}
                onClick={() => setFontSize(size)}
                sx={{ minWidth: 32, px: 0.5, fontSize: 11 }}
              >
                {size}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>

        {/* Dark/light toggle */}
        <ToggleButtonGroup
          value={darkMode ? 'dark' : 'light'}
          exclusive
          onChange={(_, val) => {
            if (val !== null) setDarkMode(val === 'dark');
          }}
          size="small"
        >
          <ToggleButton value="light" aria-label="Light mode">
            <LightModeIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="dark" aria-label="Dark mode">
            <DarkModeIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Close button */}
        <Tooltip title="Exit preview (Esc)">
          <IconButton onClick={onClose} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Scrollable content area */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: 700,
            mx: 'auto',
            color: textColor,
            transition: 'color 0.3s ease',
          }}
        >
          {/* Chapter title */}
          {currentChapter && (
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 600,
                mb: 4,
                textAlign: 'center',
                color: textColor,
              }}
            >
              {currentChapter.title}
            </Typography>
          )}

          <Divider sx={{ mb: 4, borderColor: darkMode ? '#444' : '#ddd' }} />

          {/* Paragraphs */}
          {paragraphs.data?.map((p) => (
            <ReaderParagraph
              key={p.id}
              paragraph={p}
              fontSize={fontSize}
              textColor={textColor}
              darkMode={darkMode}
            />
          ))}

          {(!paragraphs.data || paragraphs.data.length === 0) && (
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontStyle: 'italic',
                color: textColor,
                opacity: 0.6,
                textAlign: 'center',
                py: 8,
              }}
            >
              This chapter has no paragraphs yet.
            </Typography>
          )}

          {/* Chapter navigation */}
          <Divider
            sx={{ mt: 6, mb: 3, borderColor: darkMode ? '#444' : '#ddd' }}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ pb: 4 }}>
            <Button
              startIcon={<NavigateBeforeIcon />}
              disabled={!hasPrev}
              onClick={goPrev}
              sx={{ color: textColor }}
            >
              {hasPrev ? chapters[currentIndex - 1]!.title : 'Previous'}
            </Button>
            <Typography
              variant="caption"
              sx={{ alignSelf: 'center', color: textColor, opacity: 0.5 }}
            >
              Chapter {currentIndex + 1} of {chapters.length}
            </Typography>
            <Button
              endIcon={<NavigateNextIcon />}
              disabled={!hasNext}
              onClick={goNext}
              sx={{ color: textColor }}
            >
              {hasNext ? chapters[currentIndex + 1]!.title : 'Next'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

interface ReaderParagraphProps {
  paragraph: ParagraphDto;
  fontSize: number;
  textColor: string;
  darkMode: boolean;
}

function ReaderParagraph({
  paragraph,
  fontSize,
  textColor,
  darkMode: _darkMode,
}: ReaderParagraphProps) {
  const { data: images } = useParagraphImages(paragraph.id);
  const { data: audioFiles } = useParagraphAudio(paragraph.id);

  const latestAudio =
    audioFiles && audioFiles.length > 0
      ? audioFiles[audioFiles.length - 1]
      : null;

  const bannerImages = images?.filter((i) => i.position === 'banner') ?? [];
  const backgroundImages =
    images?.filter((i) => i.position === 'background') ?? [];
  const inlineImages =
    images?.filter((i) => !i.position || i.position === 'inline') ?? [];

  // For background images, use the first one as the paragraph background.
  const bgImage = backgroundImages[0];

  return (
    <Box
      sx={{
        mb: 3,
        ...(bgImage
          ? {
              backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${bgImage.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: 1,
              p: 3,
            }
          : {}),
      }}
    >
      {/* Banner images — full-width above text */}
      {bannerImages.length > 0 && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {bannerImages.map((ref) => (
            <AppImage
              key={ref.referenceId}
              src={ref.url}
              alt="Paragraph banner"
              width="100%"
              objectFit="cover"
              objectPosition="center"
              sx={{ borderRadius: 1, maxHeight: 400 }}
            />
          ))}
        </Stack>
      )}

      {/* Paragraph text */}
      <Box
        dangerouslySetInnerHTML={{ __html: paragraph.contentHtml }}
        sx={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: `${fontSize}px`,
          lineHeight: 1.8,
          color: bgImage ? '#ffffff' : textColor,
          '& p': { margin: 0, mb: 1 },
          '& em': { fontStyle: 'italic' },
          '& strong': { fontWeight: 700 },
          transition: 'font-size 0.2s ease, color 0.3s ease',
        }}
      />

      {/* Inline images */}
      {inlineImages.length > 0 && (
        <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
          {inlineImages.map((ref) => (
            <AppImage
              key={ref.referenceId}
              src={ref.url}
              alt="Paragraph illustration"
              width="100%"
              objectFit="contain"
              objectPosition="center"
              sx={{ borderRadius: 1, maxHeight: 500 }}
            />
          ))}
        </Stack>
      )}

      {/* Audio playback */}
      {latestAudio && (
        <Box sx={{ mt: 1, maxWidth: 320 }}>
          <AudioPlayer src={latestAudio.url} compact />
        </Box>
      )}
    </Box>
  );
}
